import React, { useState, useEffect } from 'react';
import {
  Typography,
  Button,
  Grid,
  TextField,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Box,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from '@mui/material';

import { useHistory } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import axios from 'axios';
import CourierLayout from './CourierLayout';
import { API } from '../../config';
import { CourierUpdatesPanel } from './ProfileManagement/CourierNotifications';

const milesOptions = ['All', '0-10', '10-25', '25-50', '50-100', '100+'];

const truckBodyTypes = [
  'All',
  'Box Truck',
  'Flatbed Truck',
  'Refrigerated Truck',
  'Dump Truck',
  'Tanker Truck',
  'Lowboy Trailer',
  'Dry Van',
  'Step Deck',
  'Car Carrier',
  'Curtain Side Truck',
  'Hazmat Tanker',
  'Hot Shot Truck',
  'Pickup Truck',
  'Cargo Van',
  'Sprinter Van',
  'Gooseneck Trailer',
  'Tilt Trailer',
  'Logging Truck',
  'Tent Truck',
  'Other',
];

const loadTypes = [
  'All',
  'General',
  'Refrigerated',
  'Oversized',
  'Hazmat',
  'High-Value',
  'Fragile',
];

const CourierDashboard = () => {
  const history = useHistory();

  const [selectedDate, setSelectedDate] = useState(null);
  const [courierType, setCourierType] = useState('Individual');
  const [truckBodyType, setTruckBodyType] = useState('All');
  const [maxLoadWeight, setMaxLoadWeight] = useState('');
  const [loadType, setLoadType] = useState('All');
  const [requiresLiftgate, setRequiresLiftgate] = useState(false);
  const [availableLoads, setAvailableLoads] = useState([]);
  const [allLoads, setAllLoads] = useState([]);
  const [milesRange, setMilesRange] = useState('All');
  const [minLenFt, setMinLenFt] = useState('');
  const [maxLenFt, setMaxLenFt] = useState('');

 useEffect(() => {
  window.scrollTo(0, 0);

  const storedType = localStorage.getItem('courierType');

  if (storedType) {
    setCourierType(storedType);
  }

  fetchAvailableLoads();
}, []);

  const redirectToProfileManagement = () => {
    history.push('/courier/profile-management');
  };

  const fetchAvailableLoads = async () => {
  try {
    const token =
      localStorage.getItem('courier_token') ||
      localStorage.getItem('token');

    if (!token) {
      history.push('/courier/sign-in');
      return;
    }

    const response = await axios.get(`${API}/api/loads`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      withCredentials: true,
    });

    const rows = response.data || [];

      const withRecommendations = rows.map((load) => {
        const miles = Number(load.distance_in_miles || 0);
        const isRecommended = load.required_truck_type && miles < 20;

        return {
          ...load,
          isRecommended,
        };
      });

      setAvailableLoads(withRecommendations);
      setAllLoads(withRecommendations);
    } catch (error) {
      console.error('Error fetching loads:', error);
    }
  };

  const handleAcceptLoad = async (orderId) => {
    try {
      const token = localStorage.getItem('token');

      await axios.post(
        `${API}/api/orders/accept`,
        { orderId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      alert('Load accepted successfully.');
      fetchAvailableLoads();
    } catch (error) {
      console.error('acceptOrder error:', error?.response?.data || error);

      const msg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Unknown error';

      alert(`Failed to accept load.\n${msg}`);
    }
  };

  const handleSearchLoads = async () => {
    const searchPayload = {
      courierType,
      truckBodyType,
      maxLoadWeight,
      loadType,
      requiresLiftgate,
      milesRange,
      selectedDate: selectedDate
        ? selectedDate.toISOString().slice(0, 10)
        : null,
      minLenFt: minLenFt ? Number(minLenFt) : null,
      maxLenFt: maxLenFt ? Number(maxLenFt) : null,
    };

    try {
      const token =
  localStorage.getItem('courier_token') ||
  localStorage.getItem('token');

if (!token) {
  history.push('/courier/sign-in');
  return;
}

const res = await axios.post(`${API}/api/search-loads`, searchPayload, {
  headers: {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  },
  withCredentials: true,
});

      const list = res?.data?.loads ?? res?.data;

      if (Array.isArray(list)) {
        const withRecommendations = list.map((load) => {
          const miles = Number(load.distance_in_miles || 0);
          const isRecommended = load.required_truck_type && miles < 20;

          return {
            ...load,
            isRecommended,
          };
        });

        setAvailableLoads(withRecommendations);
        return;
      }
    } catch (err) {
      console.warn(
        'POST /api/search-loads failed; client filter fallback',
        err?.message || ''
      );
    }

    const base = allLoads.length ? allLoads : availableLoads;

    const results = base.filter((load) => {
      if (courierType === 'Professional' && !load.required_truck_type) {
        return false;
      }

      if (courierType === 'Individual' && load.required_truck_type) {
        return false;
      }

      if (
        truckBodyType &&
        truckBodyType !== 'All' &&
        load.required_truck_type !== truckBodyType
      ) {
        return false;
      }

      if (maxLoadWeight && Number(load.weight || 0) > Number(maxLoadWeight)) {
        return false;
      }

      if (
        loadType &&
        loadType !== 'All' &&
        String(load.delivery_type || '').toLowerCase() !==
          loadType.toLowerCase()
      ) {
        return false;
      }

      if (milesRange && milesRange !== 'All') {
        const val = milesRange.trim();
        const miles = Number(load.distance_in_miles || 0);

        if (/^\d+\+$/.test(val)) {
          const min = parseInt(val, 10);

          if (!(miles >= min)) {
            return false;
          }
        } else if (/^\d+-\d+$/.test(val)) {
          const [lo, hi] = val.split('-').map(Number);

          if (!(miles >= lo && miles <= hi)) {
            return false;
          }
        }
      }

      if (selectedDate) {
        const d = new Date(load.created_at);
        const ymd = d.toISOString().slice(0, 10);
        const sel = selectedDate.toISOString().slice(0, 10);

        if (ymd !== sel) {
          return false;
        }
      }

      return true;
    });

    setAvailableLoads(results);
  };

  const estimateETA = (miles) => {
    const speed = 50;
    const hours = miles / speed;
    const days = Math.floor(hours / 24);
    const remHours = Math.round(hours % 24);

    return `${days}d ${remHours}h`;
  };

  const filteredLoads = availableLoads.filter((load) =>
    courierType === 'Individual' ? !load.required_truck_type : true
  );

  return (
    <CourierLayout>
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          sx={{
            fontSize: { xs: '1.8rem', sm: '2.1rem', md: '2.3rem' },
            fontWeight: 800,
            lineHeight: 1.15,
            letterSpacing: '-0.03em',
            color: '#0f172a',
            mb: 1,
          }}
        >
          Available Loads
        </Typography>

        <Typography
          variant="body1"
          sx={{
            color: '#64748b',
            maxWidth: 850,
            lineHeight: 1.7,
          }}
        >
          Search available loads, review shipment details, negotiate rates, and
          manage active assignments across local and long-distance routes.
        </Typography>
            </Box>

      <CourierUpdatesPanel embedded />

      <Paper
        elevation={3}
        sx={{
          p: { xs: 2.5, md: 4 },
          borderRadius: 4,
          border: '1px solid rgba(15, 23, 42, 0.08)',
          mb: 4,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: '#0f172a',
            mb: 1,
          }}
        >
          Search Filters
        </Typography>

        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              label="Account Type"
              value={courierType}
              onChange={(e) => setCourierType(e.target.value)}
              variant="outlined"
              fullWidth
            >
              <MenuItem value="Individual">Individual Driver</MenuItem>
              <MenuItem value="Professional">
                Company / Professional Carrier
              </MenuItem>
            </TextField>
          </Grid>

{/*
          <Grid item xs={12} sm={6} md={3}>
            <TextField label="Pickup Location" variant="outlined" fullWidth />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField label="Drop-off Location" variant="outlined" fullWidth />
          </Grid>
*/}
          <Grid item xs={12} sm={6} md={3}>
            <DatePicker
              selected={selectedDate}
              onChange={(date) => setSelectedDate(date)}
              dateFormat="yyyy-MM-dd"
              isClearable
              placeholderText="Select Date"
              customInput={<TextField fullWidth />}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              label="Distance"
              variant="outlined"
              fullWidth
              value={milesRange}
              onChange={(e) => setMilesRange(e.target.value)}
            >
              {milesOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {courierType === 'Professional' && (
            <>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  select
                  label="Vehicle Type"
                  variant="outlined"
                  fullWidth
                  value={truckBodyType}
                  onChange={(e) => setTruckBodyType(e.target.value)}
                >
                  {truckBodyTypes.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Max Weight (lbs)"
                  variant="outlined"
                  fullWidth
                  value={maxLoadWeight}
                  onChange={(e) => setMaxLoadWeight(e.target.value)}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  select
                  label="Shipment Type"
                  variant="outlined"
                  fullWidth
                  value={loadType}
                  onChange={(e) => setLoadType(e.target.value)}
                >
                  {loadTypes.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Min Length (ft)"
                  variant="outlined"
                  fullWidth
                  value={minLenFt}
                  onChange={(e) => setMinLenFt(e.target.value)}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Max Length (ft)"
                  variant="outlined"
                  fullWidth
                  value={maxLenFt}
                  onChange={(e) => setMaxLenFt(e.target.value)}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    backgroundColor: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={requiresLiftgate}
                        onChange={(e) =>
                          setRequiresLiftgate(e.target.checked)
                        }
                      />
                    }
                    label="Requires Liftgate"
                  />
                </Paper>
              </Grid>
            </>
          )}

          <Grid item xs={12} md={3}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSearchLoads}
              fullWidth
              size="large"
              sx={{
                minHeight: 56,
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: 2,
              }}
            >
              Search Loads
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1,
          mb: 2,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: '#0f172a',
          }}
        >
          Matching Loads
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: '#64748b',
          }}
        >
          {filteredLoads.length} load
          {filteredLoads.length === 1 ? '' : 's'} available
        </Typography>
      </Box>

      <TableContainer
        component={Paper}
        sx={{
          display: { xs: 'none', md: 'block' },
          borderRadius: 4,
          border: '1px solid rgba(15, 23, 42, 0.08)',
          overflow: 'hidden',
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f8fafc' }}>
              <TableCell sx={{ fontWeight: 700 }}>Pickup</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Drop-off</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Shipment Type</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Vehicle Type</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Distance</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Weight</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>ETA</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Match</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Action</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {filteredLoads.map((load) => (
              <TableRow key={load.order_id} hover>
                <TableCell>{load.from_address}</TableCell>
                <TableCell>{load.to_address}</TableCell>
                <TableCell>{load.delivery_type || 'Standard'}</TableCell>
                <TableCell>{load.required_truck_type || 'Any'}</TableCell>
                <TableCell>
                  {load.distance_in_miles != null
                    ? `${load.distance_in_miles} mi`
                    : 'N/A'}
                </TableCell>
                <TableCell>{load.weight || 'N/A'}</TableCell>
                <TableCell>
                  {estimateETA(load.distance_in_miles || 600)}
                </TableCell>
                <TableCell>
                  {load.isRecommended ? (
                    <Chip
                      label="Recommended"
                      size="small"
                      sx={{
                        backgroundColor: '#e0f2fe',
                        color: '#0369a1',
                        fontWeight: 700,
                      }}
                    />
                  ) : (
                    '—'
                  )}
                </TableCell>
                <TableCell>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleAcceptLoad(load.order_id)}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 700,
                      borderRadius: 2,
                    }}
                  >
                    Accept Load
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box
        sx={{
          display: { xs: 'block', md: 'none' },
          mt: 2,
        }}
      >
        {filteredLoads.map((load) => (
          <Paper
            key={load.order_id}
            sx={{
              p: 2.5,
              mb: 2,
              borderRadius: 4,
              border: '1px solid rgba(15, 23, 42, 0.08)',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: 1,
                mb: 1,
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 700,
                  color: '#0f172a',
                  lineHeight: 1.4,
                }}
              >
                {load.from_address || 'Pickup not provided'} →{' '}
                {load.to_address || 'Drop-off not provided'}
              </Typography>

              {load.isRecommended && (
                <Chip
                  label="Recommended"
                  size="small"
                  sx={{
                    backgroundColor: '#e0f2fe',
                    color: '#0369a1',
                    fontWeight: 700,
                  }}
                />
              )}
            </Box>

            <Typography variant="body2" sx={{ color: '#475569', mb: 0.5 }}>
              <strong>Shipment Type:</strong> {load.delivery_type || 'Standard'}
            </Typography>

            <Typography variant="body2" sx={{ color: '#475569', mb: 0.5 }}>
              <strong>Vehicle Type:</strong> {load.required_truck_type || 'Any'}
            </Typography>

            <Typography variant="body2" sx={{ color: '#475569', mb: 0.5 }}>
              <strong>Distance:</strong>{' '}
              {load.distance_in_miles != null
                ? `${load.distance_in_miles} mi`
                : 'N/A'}
            </Typography>

            <Typography variant="body2" sx={{ color: '#475569', mb: 0.5 }}>
              <strong>Weight:</strong> {load.weight || 'N/A'}
            </Typography>

            <Typography variant="body2" sx={{ color: '#475569', mb: 1.5 }}>
              <strong>ETA:</strong>{' '}
              {estimateETA(load.distance_in_miles || 600)}
            </Typography>

            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={() => handleAcceptLoad(load.order_id)}
              sx={{
                mt: 1,
                minHeight: 44,
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: 2,
              }}
            >
              Accept Load
            </Button>
          </Paper>
        ))}
      </Box>

      {/*
      <div style={{ textAlign: 'center', marginTop: '30px' }}>
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={redirectToProfileManagement}
        >
          Profile Management
        </Button>
      </div>
      */}
    </CourierLayout>
  );
};

export default CourierDashboard;